import React, { useEffect, useId, useRef, useState } from 'react';
import { Bold, Heading2, Italic, Link2, List, ListOrdered } from 'lucide-react';
import { applyMarkdownAction, type MarkdownAction, type MarkdownEdit } from '../../lib/markdown';
import { cn } from '../../lib/utils';
import { MarkdownContent } from './MarkdownContent';

const ACTIONS: { action: MarkdownAction; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { action: 'bold', label: 'Bold', icon: Bold },
  { action: 'italic', label: 'Italic', icon: Italic },
  { action: 'heading', label: 'Heading', icon: Heading2 },
  { action: 'bullet', label: 'Bullet list', icon: List },
  { action: 'numbered', label: 'Numbered list', icon: ListOrdered },
  { action: 'link', label: 'Link', icon: Link2 },
];

export function MarkdownField({
  label,
  hint = 'Markdown supported: bold, italic, headings, lists, and links. Line breaks are kept.',
  value,
  onChange,
  required,
  name,
  rows = 5,
  placeholder,
  className,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  name?: string;
  rows?: number;
  placeholder?: string;
  className?: string;
}) {
  const fieldId = useId();
  const hintId = `${fieldId}-hint`;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pending = useRef<MarkdownEdit | null>(null);
  const [mode, setMode] = useState<'write' | 'preview'>('write');

  useEffect(() => {
    const next = pending.current;
    const el = textareaRef.current;
    if (!next || !el) return;
    pending.current = null;
    el.focus();
    el.setSelectionRange(next.selectionStart, next.selectionEnd);
  }, [value]);

  const apply = (action: MarkdownAction) => {
    const el = textareaRef.current;
    const start = el?.selectionStart ?? value.length;
    const end = el?.selectionEnd ?? value.length;
    const next = applyMarkdownAction(value, start, end, action);
    pending.current = next;
    setMode('write');
    onChange(next.value);
  };

  return (
    <div className={className}>
      <label htmlFor={fieldId} className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">
        {label}
      </label>
      <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-1)] focus-within:border-[var(--color-accent)] focus-within:ring-2 focus-within:ring-[var(--color-accent-subtle)] transition-all duration-[var(--duration-fast)]">
        <div className="flex items-center justify-between gap-2 border-b border-[var(--color-border)] bg-[var(--color-surface-2)] px-1.5 py-1 rounded-t-[var(--radius-md)]">
          <div className="flex items-center" role="toolbar" aria-label={`${label} formatting`}>
            {ACTIONS.map(({ action, label: actionLabel, icon: Icon }) => (
              <button
                key={action}
                type="button"
                title={actionLabel}
                aria-label={actionLabel}
                onClick={() => apply(action)}
                className="inline-flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-1)] hover:text-[var(--color-text-primary)]"
              >
                <Icon className="w-3.5 h-3.5" />
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-[var(--radius-sm)] bg-[var(--color-surface-0)] p-0.5" role="group" aria-label={`${label} mode`}>
            {(['write', 'preview'] as const).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={mode === option}
                onClick={() => setMode(option)}
                className={cn(
                  'px-2 py-1 text-xs font-medium rounded-[4px]',
                  mode === option
                    ? 'bg-[var(--color-surface-1)] text-[var(--color-text-primary)] shadow-[var(--shadow-sm)]'
                    : 'text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)]',
                )}
              >
                {option === 'write' ? 'Write' : 'Preview'}
              </button>
            ))}
          </div>
        </div>
        <textarea
          id={fieldId}
          ref={textareaRef}
          name={name}
          required={required}
          rows={rows}
          value={value}
          placeholder={placeholder}
          aria-describedby={hint ? hintId : undefined}
          onChange={(ev) => onChange(ev.target.value)}
          className={cn(
            'w-full bg-transparent py-2.5 px-3.5 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] outline-none resize-y',
            mode === 'preview' && 'sr-only',
          )}
        />
        {mode === 'preview' && (
          <div className="px-3.5 py-2.5 text-sm text-[var(--color-text-secondary)]" style={{ minHeight: `${rows * 1.5}rem` }}>
            {value.trim() ? (
              <MarkdownContent text={value} />
            ) : (
              <p className="text-sm text-[var(--color-text-tertiary)]">Nothing to preview yet.</p>
            )}
          </div>
        )}
      </div>
      {hint && (
        <p id={hintId} className="text-xs text-[var(--color-text-tertiary)] mt-1">
          {hint}
        </p>
      )}
    </div>
  );
}
